// CRM seat notices — the personal payment lock (#14735) and its agency-funded sibling (#13093) — render at the top
// of #main-content on every page (layouts/goatleads/_crm_payment_lock_banner, _crm_agency_seat_banner; both carry
// .gl-crm-payment-lock-banner). A full-page data table is `position: fixed` just under the navbar
// (components/_tables.scss) and painted over them on desktop, so Leads — the landing page — never showed an
// agent that their CRM was locked or their seat was ending.
//
// The same measure/publish contract as the site and impersonation banners (application.js): this publishes where
// the last notice ENDS, in the page's own CSS pixels, as --crm-banner-bottom (0px when there is none), and every
// fixed full-page table starts no higher than that — `top: max(<its offset>, var(--crm-banner-bottom, 0px))` in
// _tables.scss and _layout.scss. On phones the table is back in document flow and the value is unused.

export const CRM_BANNER_SELECTOR = "#main-content > .gl-crm-payment-lock-banner";
export const CRM_BANNER_BOTTOM_VAR = "--crm-banner-bottom";

// The last notice's bottom edge, from the top of the document. offsetTop/offsetHeight are layout pixels inside the
// zoomed body — the same space the fixed table's `top` is written in — where getBoundingClientRect would return
// zoomed viewport pixels.
export function crmBannerBottom(root = document) {
  const banners = root.querySelectorAll(CRM_BANNER_SELECTOR);
  const last = banners[banners.length - 1];
  if (!last || !last.isConnected) return 0;

  let top = 0;
  for (let node = last; node; node = node.offsetParent) top += node.offsetTop;
  return top + last.offsetHeight;
}

export function publishCrmBannerBottom(root = document) {
  root.documentElement.style.setProperty(CRM_BANNER_BOTTOM_VAR, `${crmBannerBottom(root)}px`);
}

// Called on every turbo:load: resets the value on a page without a notice, and re-measures after layout settles
// (double requestAnimationFrame, like the site banner) and on resize — the copy wraps to more lines on a narrower
// window. The resize listener goes with the page (turbo:before-cache).
export function initCrmBannerOffset(root = document, win = window) {
  publishCrmBannerBottom(root);
  if (!root.querySelector(CRM_BANNER_SELECTOR)) return;

  win.requestAnimationFrame(() => {
    win.requestAnimationFrame(() => publishCrmBannerBottom(root));
  });
  const handleResize = () => publishCrmBannerBottom(root);
  win.addEventListener("resize", handleResize);
  root.addEventListener("turbo:before-cache", () => win.removeEventListener("resize", handleResize), { once: true });
}
